import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, IndianRupee, Heart, Mail, ArrowLeft, Check, Building2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORY_MAP, CATEGORY_FIELDS } from "@/lib/categories";
import VerifiedBadge from "@/components/locanest/VerifiedBadge";
import TierBadge from "@/components/locanest/TierBadge";
import { Star } from "lucide-react";
import ChatDrawer from "@/components/locanest/ChatDrawer";
import BookingButton from "@/components/locanest/BookingButton";
import SubscribeButton from "@/components/locanest/SubscribeButton";
import AntiOfflineNotice from "@/components/locanest/AntiOfflineNotice";
import ViewNumberButton from "@/components/locanest/ViewNumberButton";
import ShareButton from "@/components/locanest/ShareButton";
import LocationBlock from "@/components/locanest/LocationBlock";
import ExploreNearby from "@/components/locanest/ExploreNearby";
import ReadMore from "@/components/locanest/ReadMore";
import PayoutProtectionNotice from "@/components/locanest/PayoutProtectionNotice";
import ProtectedPayButton from "@/components/locanest/ProtectedPayButton";

function humanValue(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (Array.isArray(v)) return v.join(", ");
  return v?.toString();
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [isFav, setIsFav] = useState(false);
  const [message, setMessage] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);

  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => setP(data)).catch(() => setP(false));
    api.get("/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (user?.role === "tenant") {
      api.get("/favorites").then(({ data }) => setIsFav(data.some((x) => x.id === id))).catch(() => {});
    }
  }, [user, id]);

  if (p === false) return <div className="max-w-3xl mx-auto px-5 py-20 text-center">Property not found. <Link className="text-[#C85A32]" to="/listings">Back to listings</Link></div>;
  if (!p) return <div className="max-w-3xl mx-auto px-5 py-20 text-center text-[#5C6B7A]">Loading…</div>;

  const cat = CATEGORY_MAP[p.category] || { label: p.category };
  const catFieldsMeta = CATEGORY_FIELDS[p.category] || [];
  const isShortStay = p.category === "short_stay";

  const toggleFav = async () => {
    if (!user) return navigate("/login");
    try {
      if (isFav) { await api.delete(`/favorites/${id}`); setIsFav(false); toast.success("Removed from favorites"); }
      else { await api.post(`/favorites/${id}`); setIsFav(true); toast.success("Saved to favorites"); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendInquiry = async () => {
    if (!user) return navigate("/login");
    if (!message.trim()) return toast.error("Please write a message");
    setSending(true);
    try {
      await api.post("/inquiries", { property_id: id, message, visit_date: visitDate || null });
      toast.success("Inquiry sent! Owner will contact you soon.");
      setOpen(false); setMessage(""); setVisitDate("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 pb-24 lg:pb-8">
      <Link to="/listings" className="inline-flex items-center text-sm text-[#5C6B7A] hover:text-[#C85A32] mb-4" data-testid="back-link">
        <ArrowLeft size={14} className="mr-1" /> Back to listings
      </Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 min-w-0">
          {/* GALLERY */}
          <div className="rounded-lg overflow-hidden border border-[#1A1F2C]/10">
            <div className="aspect-[16/10] bg-[#1A1F2C]/5">
              <img src={p.photos?.[active] || p.photos?.[0]} alt={p.title} className="w-full h-full object-cover" data-testid="hero-photo" />
            </div>
            {p.photos?.length > 1 && (
              <div className="relative bg-[#F0EBE1]">
                <div className="flex gap-1 p-1 overflow-x-auto snap-x [scrollbar-width:thin]" data-testid="gallery-thumbs">
                  {p.photos.map((src, i) => (
                    <button key={i} onClick={() => setActive(i)} className={`w-[72px] h-[72px] md:w-24 md:h-24 shrink-0 snap-start overflow-hidden rounded ${active === i ? "ring-2 ring-[#C85A32]" : "opacity-80 hover:opacity-100"}`} data-testid={`thumb-${i}`}>
                      <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <div className="absolute top-2 right-2 text-[11px] px-2 py-0.5 rounded-full bg-black/60 text-white" data-testid="photo-counter">{active + 1} / {p.photos.length}</div>
              </div>
            )}
          </div>

          {p.videos?.length > 0 && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="prop-videos">
              {p.videos.map((v, i) => (
                <div key={v.url} className="rounded-lg overflow-hidden border border-[#1A1F2C]/10 bg-black">
                  <video src={v.url} controls playsInline preload="metadata" className="w-full aspect-video" data-testid={`prop-video-${i}`} />
                </div>
              ))}
            </div>
          )}

          {/* TITLE */}
          <div className="mt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#F0EBE1] text-[10px] uppercase tracking-[0.18em]" data-testid="prop-category">
                  <Building2 size={11} /> {cat.label}
                </div>
                <h1 className="font-display text-3xl md:text-4xl leading-tight mt-2 break-words" data-testid="prop-title">{p.title}</h1>
                <div className="mt-2 flex items-center gap-1 text-[#5C6B7A]"><MapPin size={14} /> {p.address}</div>
              </div>
              <Button variant="outline" onClick={toggleFav} className="shrink-0 rounded-md" data-testid="fav-button">
                <Heart size={16} className="mr-1.5" fill={isFav ? "#C85A32" : "none"} color={isFav ? "#C85A32" : "currentColor"} />
                {isFav ? "Saved" : "Save"}
              </Button>
            </div>

            {/* KEY FACTS */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {isShortStay ? (
                <>
                  <Fact label="₹ / night" value={<span className="inline-flex items-center"><IndianRupee size={16} />{p.category_fields?.price_per_night?.toLocaleString?.("en-IN") || 0}</span>} accent />
                  <Fact label="Min nights" value={p.category_fields?.min_nights || "-"} />
                  <Fact label="Max guests" value={p.category_fields?.max_guests || "-"} />
                  <Fact label="Check-in" value={p.category_fields?.check_in || "-"} />
                </>
              ) : (
                <>
                  <Fact label="Rent" accent value={<span className="inline-flex items-center"><IndianRupee size={16} />{p.rent?.toLocaleString?.("en-IN") || 0}<span className="text-xs text-[#5C6B7A] font-normal ml-1">/mo</span></span>} />
                  <Fact label="Deposit" value={p.deposit ? inr(p.deposit) : "—"} />
                  {/* Show top 2 category fields */}
                  {catFieldsMeta.slice(0, 2).map((f) => (
                    <Fact key={f.key} label={f.label} value={humanValue(p.category_fields?.[f.key]) || "—"} />
                  ))}
                </>
              )}
            </div>

            {/* DESCRIPTION */}
            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">About this listing</div>
              <div className="mt-3"><ReadMore text={p.description} words={60} testId="description" /></div>
            </div>

            {/* CATEGORY DETAILS */}
            {catFieldsMeta.length > 0 && (
              <div className="mt-8">
                <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">{cat.label} details</div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {catFieldsMeta.map((f) => {
                    const v = p.category_fields?.[f.key];
                    if (v === undefined || v === null || v === "") return null;
                    return (
                      <div key={f.key} className="bg-[#F0EBE1] rounded-md p-3">
                        <div className="text-xs text-[#5C6B7A]">{f.label}</div>
                        <div className="text-sm mt-1">{humanValue(v)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* AMENITIES */}
            {p.amenities?.length > 0 && (
              <div className="mt-8">
                <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Amenities</div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {p.amenities.map((a) => (
                    <div key={a} className="inline-flex items-center gap-2 text-sm">
                      <Check size={14} className="text-[#2B3A67]" /> {a}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MAP */}
            <LocationBlock property={p} />
          </div>
        </div>

        {/* SIDEBAR */}
        <aside className="lg:col-span-1 min-w-0">
          <div className="sticky top-24 bg-[#F0EBE1] border border-[#1A1F2C]/10 rounded-lg p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-[#5C6B7A]">Listed by</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="font-display text-2xl">{p.owner_name}</span>
              {p.owner_verified && <VerifiedBadge />}
              {p.owner_tier && p.owner_tier !== "new" && <TierBadge tier={p.owner_tier} />}
            </div>
            {p.owner_business_name && <div className="text-sm text-[#5C6B7A] mt-1">{p.owner_business_name}</div>}
            {(p.owner_rating_count > 0 || p.owner_completed_deals > 0) && (
              <div className="mt-2 flex items-center gap-3 text-xs text-[#5C6B7A]" data-testid="owner-rating-summary">
                {p.owner_rating_count > 0 && (
                  <span className="inline-flex items-center gap-1"><Star size={12} fill="#C85A32" color="#C85A32" /> <strong className="text-[#1A1F2C]">{p.owner_rating_avg.toFixed(1)}</strong> · {p.owner_rating_count} review{p.owner_rating_count > 1 ? "s" : ""}</span>
                )}
                {p.owner_completed_deals > 0 && <span>· {p.owner_completed_deals} deal{p.owner_completed_deals > 1 ? "s" : ""} completed</span>}
              </div>
            )}

            <div className="mt-5 space-y-2 text-sm">
              <ViewNumberButton property={p} />
              {!p.owner_phone && (!user || user?.role === "tenant") && user?.id !== p.owner_id && cfg?.subscription_enforcement && (
                <SubscribeButton variant="compact" onSubscribed={() => api.get(`/properties/${id}`).then(({ data }) => setP(data))} />
              )}
            </div>

            {user && user.id !== p.owner_id && (
              <Button onClick={() => setChatOpen(true)} variant="outline" className="w-full mt-3 rounded-md" data-testid="open-chat-btn">
                💬 Chat with owner
              </Button>
            )}
            {(!user || user?.role === "tenant") && user?.id !== p.owner_id && (
              p.is_available === false ? (
                <div className="rounded-md bg-[#C85A32]/10 border border-[#C85A32]/30 p-4 text-sm text-[#1A1F2C] mt-3" data-testid="detail-out-of-stock">
                  <div className="uppercase tracking-[0.18em] text-[10px] font-semibold text-[#C85A32]">Out of stock</div>
                  This listing is currently unavailable. The owner will mark it available again once it&rsquo;s open for new tenants.
                </div>
              ) : (
                <>
                  <BookingButton
                    property={p}
                    paymentPaused={!!cfg && !cfg.booking_advance_enforcement}
                    onBooked={() => api.get(`/properties/${id}`).then(({ data }) => setP(data))}
                  />
                  {(!user || user?.role === "tenant") && user?.id !== p.owner_id && <ProtectedPayButton property={p} />}
                </>
              )
            )}

            {cfg && !cfg.booking_advance_enforcement && p.is_available !== false && (!user || user?.role === "tenant") && user?.id !== p.owner_id && (
              <div className="mt-3 rounded-md bg-[#D4DEC6]/30 border border-[#2B3A67]/20 p-3 text-xs text-[#1A1F2C]" data-testid="advance-paused-notice">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#2B3A67] font-semibold mb-1">How it works</div>
                <strong>Send interest</strong> is free — no advance/token taken; the owner is notified instantly (in-app + email). <strong>Payment to Broker</strong> is optional: once you agree an amount, pay it via LocaNest so the money is protected and transferred to the owner within 3–4 days.
              </div>
            )}

            <div className="mt-3">
              <ShareButton property={p} variant="full" testId="detail-share-btn" />
            </div>

            <Dialog open={open} onOpenChange={(v) => { if (v && !user) { navigate("/login"); return; } setOpen(v); }}>
              <DialogTrigger asChild>
                <Button className="w-full mt-6 bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="contact-owner-btn">
                  <Mail size={15} className="mr-2" /> Contact Owner
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send inquiry to {p.owner_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Textarea placeholder="Hi, I'm interested in this listing. When can I visit?" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="inquiry-message" />
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#5C6B7A]">Preferred visit date</label>
                    <Input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} data-testid="inquiry-visit-date" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={sendInquiry} disabled={sending} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="inquiry-send-btn">
                    {sending ? "Sending…" : "Send inquiry"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="mt-4 text-xs text-[#5C6B7A] leading-relaxed">
              {cfg?.booking_advance_enforcement
                ? "Reserve securely via Cashfree. Owner reject → full refund. Tenant cancel → non-refundable, per LocaNest policy."
                : "Free launch — no advance/token needed. Contact the owner and, when you finalise, please complete the deal through LocaNest so both sides stay protected."}
            </div>
            <AntiOfflineNotice className="mt-3" />
            {(!user || user?.role === "tenant") && user?.id !== p.owner_id && <PayoutProtectionNotice className="mt-3" compact />}
          </div>
        </aside>
      </div>

      <ExploreNearby propertyId={id} city={p.city} />

      {/* Mobile sticky CTA bar — sits above the bottom nav so the key action is always one tap away */}
      {(!user || user?.role === "tenant") && user?.id !== p.owner_id && p.is_available !== false && (
        <div className="lg:hidden fixed bottom-16 inset-x-0 z-40 bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-[#1A1F2C]/10 px-4 py-2.5 flex items-center gap-3" data-testid="mobile-cta-bar">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B7A]">{isShortStay ? "Per night" : "Rent / month"}</div>
            <div className="font-display text-xl text-[#C85A32] leading-none truncate">{isShortStay ? inr(p.category_fields?.price_per_night || 0) : inr(p.rent || 0)}</div>
          </div>
          {user && (
            <Button onClick={() => setChatOpen(true)} variant="outline" size="icon" className="shrink-0 rounded-md h-11 w-11" aria-label="Chat with owner" data-testid="mobile-chat-btn">
              <MessageCircle size={18} />
            </Button>
          )}
          <BookingButton
            property={p}
            paymentPaused={!!cfg && !cfg.booking_advance_enforcement}
            onBooked={() => api.get(`/properties/${id}`).then(({ data }) => setP(data))}
            className="flex-1 h-11 bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md"
          />
        </div>
      )}

      <ChatDrawer propertyId={id} open={chatOpen} onOpenChange={setChatOpen} onOpened={() => api.get(`/properties/${id}`).then(({data}) => setP(data))} />
    </div>
  );
}

function Fact({ label, value, accent }) {
  return (
    <div className="bg-[#F0EBE1] rounded-md p-4">
      <div className="text-xs uppercase tracking-[0.18em] text-[#5C6B7A]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${accent ? "text-[#C85A32]" : ""}`}>{value}</div>
    </div>
  );
}
